<template>
  <div>
    <!-- 主 Modal -->
    <div v-if="!showSettingsPanel" class="modal-overlay" >
      <div class="modal-container">
        <!-- 標題列 -->
        <div class="modal-header">
          <h3 class="modal-title">{{ t('gameActManager.createActivity') }}</h3>
          <button class="close-btn" @click="handleClose">×</button>
        </div>

        <!-- 內容區 -->
        <div class="modal-body">
          <div class="modal-body-layout">
            <!-- 左側：遊戲縮圖 -->
            <div class="modal-body-left">
              <div class="game-preview-card">
                <div
                  class="game-preview-image"
                  :class="{ 'is-aigame': gameSettings.currentGame === 'aigame' }"
                  @click="gameSettings.currentGame === 'aigame' && triggerFileInput()"
                >
                  <img :src="previewImageSrc" :alt="getCurrentGameName()">
                  <div v-if="gameSettings.currentGame === 'aigame'" class="thumbnail-overlay">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="32" height="32">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </div>
                  <input
                    v-if="gameSettings.currentGame === 'aigame'"
                    ref="thumbnailFileInput"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    class="thumbnail-file-input"
                    @change="handleThumbnailChange"
                  />
                </div>
                <div v-if="uploadError" class="thumbnail-error">{{ uploadError }}</div>
              </div>
            </div>

            <!-- 右側：表單欄位 -->
            <div class="modal-body-right">
              <!-- 活動名稱 -->
              <div class="form-group">
                <div class="input-with-counter">
                  <input
                    v-model="formData.activityName"
                    type="text"
                    class="form-input"
                    :placeholder="t('gameActManager.activityNamePlaceholder')"
                    maxlength="20"
                  />
                  <span class="char-counter">{{ formData.activityName.length }}/20</span>
                </div>
                <div v-if="errors.activityName" class="error-text">{{ errors.activityName }}</div>
              </div>

              <!-- 遊戲詳細內容設定入口 -->
              <div class="form-group">
                <div class="setting-link" @click="openGameSettings">
                  <span>{{ t('gameActManager.gameSettingsEntry') }}</span>
                  <svg-icon icon-class="arrow-next" class="arrow-icon" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按鈕 -->
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="handleClose">{{ t('gameActManager.cancel') }}</button>
          <button class="btn btn-confirm" @click="handleSubmit">{{ t('gameActManager.confirm') }}</button>
        </div>

        <!-- 步驟三引導 -->
        <GuideStep3 ref="guideStep3Ref" :activity-count="activityCount" />
      </div>
    </div>

    <!-- 遊戲設定面板 -->
    <GameSettingsPanel
      v-if="showSettingsPanel"
      :settings="gameSettings"
      :activity-name="formData.activityName"
      :question-type="questionType"
      :question-type-label="questionTypeLabel"
      :activity-count="activityCount"
      @back="closeGameSettings"
      @update="updateGameSettings"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import { reactive, computed, inject, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMainStore } from '../../stores/mainStore';
import SvgIcon from '../../components/SvgIcon.vue';
import GameSettingsPanel from './GameSettingsPanel.vue';
import GuideStep3 from './GuideStep3.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import { useGameThumbnailUpload } from '@/composable/useGameThumbnailUpload';

const { t } = useI18n();
const store = useMainStore();

// Props
const props = defineProps({
  questionType: {
    type: String,
    required: true,
    validator: (value) => ['recognition', 'trueFalse', 'multiChoice', 'aiGenerated'].includes(value)
  },
  // 從父元件帶入建立者資訊
  creator: {
    type: Object,
    required: false,
    default: () => ({ id: '', name: '' })
  },
  // 從父元件帶入「遊戲詳細內容設定」的預設值
  defaultSettings: {
    type: Object,
    required: false,
    default: () => ({ playerCount: 1, currentGame: 'mole', allowTemplateChange: false, gameParameters: {} })
  },
  // 活動數量（用於步驟二引導）
  activityCount: {
    type: Number,
    default: 0
  }
});

// Emits
const emit = defineEmits(['close', 'created']);

// Inject
const $Message = inject('$Message');

// 遊戲設定面板顯示狀態
const showSettingsPanel = ref(false);

// 步驟三引導 ref
const guideStep3Ref = ref(null);

// 縮圖上傳
const thumbnailFileInput = ref(null);
const pendingThumbnailFile = ref(null);
const pendingThumbnailPreviewUrl = ref(null);
const { isUploading: isThumbnailUploading, uploadError, uploadThumbnail } = useGameThumbnailUpload();

const previewImageSrc = computed(() => {
    if (pendingThumbnailPreviewUrl.value) return pendingThumbnailPreviewUrl.value;
    return getCurrentGameImage();
});

const triggerFileInput = () => {
    thumbnailFileInput.value?.click();
};

const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    pendingThumbnailFile.value = file;
    // 本機預覽
    if (pendingThumbnailPreviewUrl.value) URL.revokeObjectURL(pendingThumbnailPreviewUrl.value);
    pendingThumbnailPreviewUrl.value = URL.createObjectURL(file);
};

// 新增活動時不需要短網址相關功能（短網址在更新活動時才使用）

// 遊戲設定資料（以父元件預設值初始化）
const gameSettings = reactive({
  playerCount: props.defaultSettings.playerCount ?? 1,
  currentGame: props.defaultSettings.currentGame ?? 'mole',
  allowTemplateChange: props.defaultSettings.allowTemplateChange ?? false,
  gameParameters: props.defaultSettings.gameParameters ?? {}
});

// 表單資料
const formData = reactive({
  activityName: t('gameActManager.activityNamePlaceholder'),
  questionType: props.questionType,
  classRosterSource: 'general', // 預設匿名（後續擴展）
  enableEntry: false, // 預設關閉（後續擴展）
  currentGame: gameSettings.currentGame,
  playerCount: gameSettings.playerCount
});

// 錯誤訊息
const errors = reactive({
  activityName: ''
});

// 題目類型標籤
const questionTypeLabel = computed(() => {
  const labels = {
    recognition: t('gameActManager.recognition'),
    trueFalse: t('gameActManager.trueFalse'),
    multiChoice: t('gameActManager.multiChoice'),
    aiGenerated: t('gameActManager.aiGenerated')
  };
  return labels[props.questionType] || '';
});

// 驗證表單
const validateForm = () => {
  errors.activityName = '';

  if (!formData.activityName || formData.activityName.trim() === '') {
    errors.activityName = t('gameActManager.activityNameRequired');
    return false;
  }

  if (formData.activityName.length > 20) {
    errors.activityName = t('gameActManager.activityNameTooLong');
    return false;
  }

  return true;
};

// 取得預設遊戲參數（和 GameParametersForm 的預設值保持一致）
const getDefaultGameParameters = (gameType) => {
  if (gameType === 'mole') {
    return {
      topic: 'Choose Fruits (Demo)',
      vocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon', 'strawberry', 'blueberry', 'pineapple', 'kiwi', 'mango'],
      errorVocabularyList: ['pen', 'pencil', 'notebook', 'eraser', 'ruler', 'scissors', 'calculator', 'backpack', 'paper', 'stapler'],
      gameDuration: 30,
      background: 'forest',
      moleSpeed: 2500
    };
  } else if (gameType === 'star') {
    return {
      topic: 'Choose Fruits (Demo)',
      vocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon', 'strawberry', 'blueberry', 'pineapple', 'kiwi', 'mango'],
      errorVocabularyList: ['pen', 'pencil', 'notebook', 'note', 'notebook', 'notebook', 'notebook', 'notebook', 'notebook', 'notebook'],
      gameDuration: 40,
      background: 'desert',
      gameSpeed: 2
    };
  } else if (gameType === 'maze') {
    // 迷宮遊戲預設參數
    return {
      topic: '迷宮遊戲',
      questionList: [
        {
          text: 'What is the capital of France?',
          options: [
            { text: 'Paris', isCorrect: true },
            { text: 'London', isCorrect: false },
            { text: 'Berlin', isCorrect: false },
            { text: 'Madrid', isCorrect: false }
          ]
        },
        {
          text: 'Which planet is known as the Red Planet?',
          options: [
            { text: 'Mars', isCorrect: true },
            { text: 'Venus', isCorrect: false },
            { text: 'Jupiter', isCorrect: false }
          ]
        }
      ]
    };
  } else if (gameType === 'snake') {
    // 貪食蛇遊戲預設參數
    return {
      topic: '英文單字挑戰',
      vocabularyList: ['computer', 'keyboard', 'monitor', 'mouse', 'printer', 'scanner', 'laptop', 'tablet', 'router', 'speaker'],
      errorVocabularyList: ['chair', 'table', 'window', 'door', 'lamp', 'book', 'pen', 'paper', 'desk', 'clock'],
      difficulty: 'medium',
      gameDuration: 60,
      boardSize: 20,
      snakeSpeed: 200,
      foodCount: 8,
      wordBatchRefreshSec: 8,
      initialSnakeLength: 3
    };
  } else if (gameType === 'tf') {
    // 火箭升空遊戲（是非題）預設參數
    return {
      topic: '常識測驗',
      questionList: [
        {
          "text": "An apple is a kind of fruit.",
          "isTrue": true
        },
        {
          "text": "The number five is bigger than the number nine.",
          "isTrue": false
        },
        {
          "text": "You use your eyes to see.",
          "isTrue": true
        }
      ],
      gameDuration: 10,
      questionDuration: 10,
      answerMode: 'sync'
    };
  } else if (gameType === 'aigame') {
    // 🎨 自製遊戲預設參數（完全自由的 HTML/CSS/JavaScript）
    return {
      customCode: '' // 使用者自定義的完整遊戲代碼
    };
  }
  return {};
};

// 提交表單
const handleSubmit = async () => {
  // 隱藏步驟三引導
  if (guideStep3Ref.value) {
    guideStep3Ref.value.hideGuide();
  }

  if (!validateForm()) {
    return;
  }

  try {
    // 如果使用者沒進入遊戲設定面板，使用預設值
    const finalGameParameters = Object.keys(gameSettings.gameParameters || {}).length > 0
      ? gameSettings.gameParameters
      : getDefaultGameParameters(gameSettings.currentGame);

    // 🎨 自製遊戲特殊處理：完全自由的 HTML/CSS/JavaScript
    let gameContentSettings = {};

    if (gameSettings.currentGame === 'aigame' && props.questionType === 'aiGenerated') {
      // 自製遊戲：只存儲使用者的自定義代碼
      gameContentSettings = {
        customCode: finalGameParameters.customCode || '' // 使用者自定義的完整遊戲代碼
      };
    } else {
      // 一般遊戲：使用原有邏輯
      gameContentSettings = finalGameParameters;
    }

    // 準備 API 請求資料
    const requestData = {
      activityName: formData.activityName,
      creator: {
        id: (props.creator && props.creator.id) ? props.creator.id : (store.profile?.id || ''),
        name: (props.creator && props.creator.name) ? props.creator.name : (store.profile?.name || '')
      },
      currentGame: gameSettings.currentGame,
      questionType: formData.questionType,
      gameContentSettings: JSON.stringify(gameContentSettings),
      classRosterSource: formData.classRosterSource,
      classRoster: JSON.stringify({ students: [] }), // 目前為空，後續擴展
      shortUrlCode: '', // 新增活動時不產生短網址
      enableEntry: false, // 新增活動時預設不開放進入
      playerCount: gameSettings.playerCount,
      allowTemplateChange: gameSettings.allowTemplateChange
    };

    console.log('提交活動資料:', requestData);

    // 呼叫 API 建立活動
    const response = await gameActivityAPI.createGameActivity(requestData);

    console.log('建立活動成功:', response);

    // 若有待上傳的縮圖，上傳後更新 DB
    if (pendingThumbnailFile.value && response.id) {
        const thumbnailUrl = await uploadThumbnail(pendingThumbnailFile.value, response.id);
        if (thumbnailUrl) {
            await gameActivityAPI.updateGameActivity({ id: response.id, thumbnailUrl });
            response.thumbnailUrl = thumbnailUrl;
        }
    }

    emit('created', response);
  } catch (error) {
    console.error('建立活動失敗:', error);

    // 檢查是否為活動數量超限錯誤
    const isLimitExceeded = error.response?.data?.errorCode === 'ACTIVITY_LIMIT_EXCEEDED' ||
                           error.response?.data?.message?.includes('已達活動數量上限') ||
                           error.response?.data?.message?.includes('已达活动数量上限');

    if (isLimitExceeded) {
      const maxLimit = error.response?.data?.maxLimit || 120;
      $Message.warning({
        content: t('gameActManager.activityLimitExceeded', { maxLimit }),
        duration: 6,
        closable: true
      });
    } else {
      $Message.error(error.message || t('gameActManager.activityCreateFailed'));
    }
  }
};

// 關閉 Modal
const handleClose = () => {
  emit('close');
};

// 開啟遊戲詳細設定
const openGameSettings = () => {
  // 進入遊戲設定時，將活動名稱同步到主題欄位
  if (!gameSettings.gameParameters) {
    gameSettings.gameParameters = {};
  }
  gameSettings.gameParameters.topic = formData.activityName;
  showSettingsPanel.value = true;
};

// 關閉遊戲設定面板
const closeGameSettings = () => {
  // 返回時，如果主題有變更，同步回活動名稱
  if (gameSettings.gameParameters && gameSettings.gameParameters.topic) {
    formData.activityName = gameSettings.gameParameters.topic;
  }
  showSettingsPanel.value = false;
};

// 更新遊戲設定
const updateGameSettings = (newSettings) => {
  Object.assign(gameSettings, newSettings);
  formData.currentGame = gameSettings.currentGame;
  formData.playerCount = gameSettings.playerCount;
};

// 取得當前遊戲圖片
const getCurrentGameImage = () => {
  const images = {
    'mole': require('@/assets/HTwbActAssets/home/cover-molegame.webp'),
    'star': require('@/assets/HTwbActAssets/home/cover-pvpgame.webp'),
    'tf': require('@/assets/HTwbActAssets/home/cover-tfgame.webp'),
    'snake': require('@/assets/HTwbActAssets/home/cover-snakegame.webp'),
    'maze': require('@/assets/HTwbActAssets/home/cover-mazegame.webp'),
    'aigame': require('@/assets/HTwbActAssets/home/cover-aigame.webp')
  };
  return images[gameSettings.currentGame] || images['mole'];
};

// 取得當前遊戲名稱
const getCurrentGameName = () => {
  const names = {
    'mole': t('gameActManager.moleGame'),
    'star': t('gameActManager.starGame'),
    'tf': t('gameActManager.rocketGame'),
    'snake': t('gameActManager.snakeGame'),
    'maze': t('gameActManager.mazeGame'),
    'aigame': t('gameActManager.aiGame')
  };
  return names[gameSettings.currentGame] || '';
};

// 組件掛載時自動開啟遊戲詳細設定
onMounted(() => {
  openGameSettings();
});
</script>

<style lang="less" scoped>
@import "../../assets/color.less";
@import "../../assets/gameActManager.less";

// Modal 遮罩層
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 15vh;
  z-index: 9999;
}

// Modal 容器
.modal-container {
  position: relative;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: visible;
}

// 標題列
.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.2;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #666;
  }
}

// 內容區
.modal-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

// Modal Body 佈局（左右分欄）
.modal-body-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.modal-body-left {
  display: flex;
  flex-direction: column;
}

// 遊戲預覽卡片（左側縮圖，無邊框）
.game-preview-card {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.game-preview-image {
  width: 100%;
  height: 240px;
  overflow: hidden;
  background-color: #f5f5f5;
  position: relative;

  @media (max-width: 768px) {
    height: 180px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  &.is-aigame {
    cursor: pointer;
  }

  .thumbnail-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &.is-aigame:hover .thumbnail-overlay {
    opacity: 1;
  }

  .thumbnail-file-input {
    display: none;
  }
}

.thumbnail-error {
  margin-top: 6px;
  font-size: 12px;
  color: #ff4d4f;
  text-align: center;
}

.modal-body-right {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  margin-bottom: 0;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.6;
}

.input-with-counter {
  position: relative;
  width: 100%;
}

.form-input {
  width: 100%;
  padding: 5px 12px;
  font-size: 20px;
  line-height: 1.6;
  border: 2px solid #d9d9d9;
  border-radius: 6px;
  transition: border-color 0.2s;
  color:black;
  font-weight: bolder;

  &:focus {
    outline: none;
    border-color: @game-primary-color;
  }

  &::placeholder {
    color: #bfbfbf;
  }
}

.char-counter {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #999;
  pointer-events: none;
  line-height: 1.6;
}

.form-select {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.6;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: @light-color;
  }
}

.readonly-value {
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.6;
  background-color: #f5f5f5;
  border-radius: 6px;
  color: #666;
}

.error-text {
  margin-top: 6px;
  font-size: 13px;
  color: #ff4d4f;
  line-height: 1.6;
}

// 遊戲詳細內容設定入口
.setting-link {
  padding: 12px 16px;
  font-size: 16px;
  line-height: 1.6;
  color: @game-primary-color;
  background-color: #f9f9fb;
  border: 2px solid @game-primary-color;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 700;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  .arrow-icon {
    font-size: 18px;

    :deep(svg) {
      fill: @game-primary-color;
      stroke: @game-primary-color;
    }
  }

  &:hover {
    background-color: @game-active-color;
    border-color: @game-hover-color;
    color: @game-hover-color;

    .arrow-icon :deep(svg) {
      fill: @game-hover-color;
      stroke: @game-hover-color;
    }
  }
}

// Toggle Switch（參照截圖 #2 的綠色開關）
.toggle-switch {
  display: inline-flex;
  align-items: center;
}

.toggle-input {
  display: none;
}

.toggle-label {
  position: relative;
  width: 44px;
  height: 24px;
  background-color: #d9d9d9;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background-color: white;
    border-radius: 50%;
    transition: transform 0.3s;
  }
}

.toggle-input:checked + .toggle-label {
  background-color: @game-primary-color; // 主題色
}

.toggle-input:checked + .toggle-label::after {
  transform: translateX(20px);
}

// Tips 提示框（參照截圖 #2）
.tips-box {
  margin-top: 20px;
  padding: 12px 16px;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
}

.tips-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #0050b3;

  strong {
    font-weight: 600;
  }
}

// 開放進入活動橫向排列
.enable-entry-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-label-large {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 0;
  line-height: 1.6;
}

// 底部按鈕
.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.btn {
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1.6;

  &:active {
    transform: translateY(1px);
  }
}

.btn-cancel {
  background-color: #f5f5f5;
  color: #666;

  &:hover {
    background-color: #e8e8e8;
  }
}

.btn-publish-full {
  width: 100%;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1.6;
  background-color: @game-primary-color;
  color: white;

  &:hover {
    background-color: @game-hover-color;
  }

  &:active {
    transform: translateY(1px);
  }
}

.btn-confirm {
  background-color: @game-primary-color;
  color: white;

  &:hover {
    background-color: @game-hover-color;
  }
}

// 短網址顯示區（參考 HTwbCardHome 樣式）
.url-wrap {
  background-color: #fff9c4; // 黃色背景
  padding: 10px;
  color: @game-primary-color;
  margin: 20px 0px 10px 0px;
  font-weight: bold;
  border-radius: 5px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.url-wrap-centered {
  justify-content: center;
}

.url-title {
  color: #333 !important;
  word-break: break-word;
  white-space: normal;
}

.short-url {
  color: black !important;
  word-break: break-word;
  font-size: 28px;
  white-space: nowrap;
  background-color: #fff;
  padding: 5px;
  border-radius: 3px;
  font-weight: bold;
}

.copy-btn {
  color: #333;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
}
</style>
